/* Arcade presentation and routing. Cart writes stay in Dawn's ProductForm. */
(() => {
  const make = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = text;
    return node;
  };
  const safeFact = text => !/\bages?\b|\byears?\s*(?:old|and\s*(?:up|over)|\+)|\b\d+\s*\+|best[ -]?sell(?:er|ing)|#\s*1/i.test(text);
  const textOf = node => node.textContent.replace(/\s+/g, ' ').trim();
  const productFacts = description => {
    const body = new DOMParser().parseFromString(description || '', 'text/html').body;
    const result = { features: [], includes: [], specs: [] };
    let group = '';
    for (const node of body.children) {
      const text = textOf(node).replace(/:$/, '');
      if (/^features$/i.test(text)) group = 'features';
      else if (/^includes|^what.?s included$/i.test(text)) group = 'includes';
      else if (/^specifications?|^dimensions(?:\s*&\s*setup)?$/i.test(text)) group = 'specs';
      else if (group && /^(UL|OL)$/.test(node.tagName)) {
        [...node.children].filter(n => n.tagName === 'LI').forEach(li => {
          const fact = textOf(li);
          if (!fact || !safeFact(fact)) return;
          // A dimension without a source unit is not a usable specification.
          if (/dimensions?|measurements?|length|width|height|thickness/i.test(fact) && /\d/.test(fact) && !/["”″]|\binch(?:es)?\b|\b(?:cm|mm|feet|ft)\b/i.test(fact)) return;
          result[group].push(fact);
        });
      }
    }
    const setup = result.features.filter(f => /assembl|setup|set up|batter|power/i.test(f));
    result.specs = [...new Set([...result.specs, ...setup])];
    return result;
  };

  class Arcade extends HTMLElement {
    connectedCallback() {
      const initial = this.querySelector('[data-product]');
      if (!initial || this.lifecycle) return;
      this.lifecycle = new AbortController();
      this.room = this.dataset.room || 'arcade';
      this.minimumPrice = Number(this.dataset.minimumPrice || 2995);
      this.strings = JSON.parse(this.querySelector('[data-strings]').textContent);
      this.bundleArtwork = JSON.parse(this.querySelector('[data-bundle-artwork]')?.textContent || '{}');
      this.bundleImageFrames = new Map();
      this.representatives = JSON.parse(this.querySelector('[data-games]').textContent).filter(game => game.handle && game.id);
      const catalog = JSON.parse(this.querySelector('[data-catalog]').textContent);
      this.games = new Map([...catalog, ...this.representatives].map(game => [game.handle, game]));
      this.active = JSON.parse(initial.textContent);
      this.products = new Map();
      this.productRequests = new Map();
      this.discoverRevision = 0;
      this.base = this.dataset.root || window.Shopify.routes.root;
      this.moneyPattern = JSON.parse(this.querySelector('[data-money-format]').textContent);
      this.form = this.querySelector('.ht-arcade__form');
      this.media = this.querySelector('[data-gallery]');
      this.rcFamily = this.hasAttribute('data-rc-family') && window.HobbytronRcFamily ? new window.HobbytronRcFamily(this) : null;
      if (this.room === 'arcade') this.arcadeRosterReady = false;
      if (this.rcFamily && new URL(location.href).searchParams.has('product')) this.setAttribute('data-entry-pending', '');
      if (this.room === 'arcade' && new URL(location.href).searchParams.has('product')) this.setAttribute('data-entry-pending', '');
      if ((this.room === 'rc-garage' && !this.rcFamily) || (this.room === 'playsets' && !this.hasAttribute('data-playsets-master'))) {
        if (new URL(location.href).searchParams.has('product')) this.setAttribute('data-entry-pending', '');
      }
      if (this.hasAttribute('data-entry-pending')) this.blocked = true;
      this.offer = this.rcFamily ? 'selector:street-legends' : 'single';
      this.progressiveSelections = new Map();
      this.progressiveRevision = 0;
      this.offerChoiceRevision = 0;
      this.bundleRevision = 0;
      this.flyingSquad = window.HobbytronCampaignSelection && (this.rcFamily || this.room === 'arcade' || this.hasAttribute('data-flying-master') || this.hasAttribute('data-playsets-master')) ? new window.HobbytronCampaignSelection(this) : this.hasAttribute('data-playsets-master') && window.HobbytronPlaysetsMaster ? new window.HobbytronPlaysetsMaster(this) : this.room === 'flying-figures' && window.HobbytronFlyingSquad ? new (this.hasAttribute('data-flying-master') ? window.HobbytronFlyingMaster : window.HobbytronFlyingSquad)(this) : null;
      this.bundleConfigPromise = this.hasAttribute('data-playsets-master') ? Promise.resolve(null) : fetch(this.dataset.bundleConfigUrl, { signal:this.lifecycle.signal }).then(r => { if (!r.ok) throw new Error('Bundle configuration unavailable'); return r.json(); }).then(registry => {
        const config = registry.rooms?.[this.room];
        if (!config) throw new Error('Room configuration unavailable');
        this.merchandisingPriority = registry.merchandising_priority?.spiderman || null;
        this.bundleConfig = config;
        if (this.room === 'arcade') {
          const paidCategories = new Set(['Air Hockey', 'Foosball', 'Pool']);
          const allowed = new Set(Object.entries(config.bundles || {}).filter(([handle, definition]) =>
            definition.eligible === true && definition.enabled === true &&
            definition.components?.some(component => component.handle === handle && paidCategories.has(component.category))
          ).map(([handle]) => handle));
          this.games = new Map([...this.games].filter(([handle]) => allowed.has(handle)));
          this.representatives = this.representatives.filter(game => allowed.has(game.handle));
          this.arcadeRosterReady = true;
        }
        if (this.rcFamily) this.rcFamily.configure(config.supercar_family);
        else {
          const family = this.room === 'rc-garage' ? config.supercar_family : null;
          const requested = new URL(location.href).searchParams.get('product') || this.dataset.defaultHandle;
          const model = family?.models.find(model => model.standalone_handle === requested);
          if (model || requested === family?.handle) {
            const url = new URL(location.href); url.searchParams.set('view', family.view); url.searchParams.set('product', family.handle);
            url.searchParams.set('variant', model?.variant_id || url.searchParams.get('variant') || family.default_variant_id);
            this.routeRedirectPending = true;
            location.replace(url.href); return config;
          }
          this.applyOfferPresentation(config.presentation);
        }
        this.renderDiscoverMore();
        this.configureProgressiveOffers(config);
        return config;
      }).catch(() => null);
      this.requestId = 0;
      this.busy = false;
      const opts = { signal: this.lifecycle.signal };
      this.addEventListener('click', this.handleClick.bind(this), opts);
      this.querySelectorAll('[data-offer-radio]').forEach(radio => radio.addEventListener('change', () => {
        if (this.busy || this.purchasing || radio.disabled) return;
        this.offerChoiceRevision++;
        this.offer = radio.value;
        this.quantity(this.form.elements.quantity.value);
        if (this.offer === 'bundle' && Date.now() - this.bundleCheckedAt > 30000) this.refreshBundle({ preserveOffer:true });
        this.syncControls();
      }, opts));
      const recheckBundle = () => {
        if (!document.hidden && !this.busy && !this.purchasing && !this.bundlePending && this.offer === 'bundle' && Date.now() - this.bundleCheckedAt > 60000) this.refreshBundle({ preserveOffer:true });
      };
      window.addEventListener('focus', recheckBundle, opts);
      document.addEventListener('visibilitychange', recheckBundle, opts);
      this.bundleTimer = setInterval(recheckBundle, 60000);
      window.addEventListener('popstate', () => this.restoreRoute(), opts);
      window.addEventListener('pageshow', event => { if (event.persisted) { this.purchasing = false; this.products.clear(); this.restoreRoute(); } }, opts);
      this.form.elements.quantity.addEventListener('change', () => this.quantity(this.form.elements.quantity.value), opts);
      this.querySelector('[data-variant-select]').addEventListener('change', event => {
        if (this.busy || this.purchasing) return;
        this.selectVariant(event.target.value);
        this.writeRoute(this.active.handle, 'replace', this.variant?.id);
        this.refreshBundle();
      }, opts);
      this.media.addEventListener('scroll', this.trackMedia.bind(this), { ...opts, passive: true });
      this.media.addEventListener('keydown', event => {
        if (event.target !== this.media || !['ArrowRight', 'ArrowLeft'].includes(event.key)) return;
        event.preventDefault(); this.navigateMedia(this.mediaIndex() + (event.key === 'ArrowRight' ? 1 : -1));
      }, opts);
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, payload => {
        if (this.purchasing && payload.source === 'product-form' && String(payload.productVariantId) === String(this.variant?.id)) this.added = true;
      });
      this.querySelectorAll('[data-js-only]').forEach(node => { node.hidden = false; });
      this.renderProduct(this.active, new URL(location.href).searchParams.get('variant'));
      const restored = this.restoreRoute();
      if (this.flyingSquad?.campaignDrawer && !this.flyingSquad.master) Promise.resolve(restored).then(() => this.flyingSquad.initialize());
      this.setAttribute('data-ready', '');
    }
    disconnectedCallback() {
      this.lifecycle?.abort(); this.productRequests?.forEach(request => request.controller.abort()); this.unsubscribe?.();
      clearInterval(this.bundleTimer);
      cancelAnimationFrame(this.mediaFrame); this.lifecycle = null;
    }
    t(key) { return this.strings[key] || key; }
    isProgressiveOffer(value = this.offer) { return typeof value === 'string' && value.startsWith('selector:'); }
    isGuardedOffer(value = this.offer) { return value === 'bundle' || this.isProgressiveOffer(value); }
    activeProgressiveTier() {
      if (!this.isProgressiveOffer()) return null;
      if (this.rcFamily) return this.rcFamily.config?.bundle || null;
      const id = this.offer.slice('selector:'.length);
      return this.progressiveDefinition()?.source?.tiers?.find(tier => tier.id === id) || null;
    }
    playsetAnchorVariant(config, handle = this.active?.handle) {
      if (this.room !== 'playsets') return null;
      const themes = config?.themed_bundle_candidates?.themes;
      const theme = themes && Object.values(themes).find(candidate => candidate?.primary?.handle === handle);
      return theme?.primary?.variant_id || null;
    }
    progressiveDefinition(config = this.bundleConfig) {
      const engine = config?.selector_offer_engine;
      if (!engine?.enabled || !engine.display_source_handles?.includes(this.active?.handle)) return null;
      const source = engine.sources?.[this.active.handle], pool = source && engine.selector_pools?.[source.selector_pool];
      if (!source || !pool) return null;
      const tiers = source.tiers || engine.tier_templates?.[source.tier_template] || engine.tier_templates?.[source.selector_pool];
      return Array.isArray(tiers) ? { engine, source:{ ...source, tiers }, pool } : null;
    }
    applyOfferPresentation(presentation) {
      if (!presentation) return;
      this.offerPresentation = presentation;
      const lines = (target, values) => {
        target.replaceChildren();
        (values || []).forEach((value, index) => { if (index) target.append(document.createElement('br')); target.append(document.createTextNode(value)); });
      };
      this.querySelectorAll('[data-bundle-title-top]').forEach(n => { n.textContent = presentation.title[0]; });
      this.querySelector('[data-bundle-title-bottom]').textContent = presentation.title[1];
      this.querySelector('.arcade-offer__comic').setAttribute('aria-label', presentation.label);
      lines(this.querySelector('[data-bundle-badge-copy]'), presentation.badge);
      this.querySelector('.arcade-offer__subheadline').textContent = presentation.subheadline;
      this.querySelector('.arcade-offer__single-copy strong').textContent = presentation.single_title;
      this.querySelector('.arcade-offer__single-caption').textContent = presentation.single_copy;
      this.querySelector('[data-retail-label]').textContent = presentation.retail_label;
      this.querySelector('[data-saving-label]').textContent = presentation.savings_label;
      this.querySelector('[data-gift-label]').textContent = presentation.gift_label || '';
      this.querySelector('[data-gift-copy]').textContent = presentation.gift_copy || '';
      const icons = this.querySelector('[data-offer-icons]').content;
      this.querySelector('.arcade-offer__benefits').replaceChildren(...presentation.benefits.map(benefit => {
        const item = make('span'), icon = icons.querySelector(`[data-icon="${benefit.icon}"] svg`), copy = make('span');
        if (icon) item.append(icon.cloneNode(true));
        lines(copy, benefit.lines); item.append(copy); return item;
      }));
    }
    async selectorProduct(entry) {
      try {
        const product = await this.loadProduct(entry.handle);
        const variant = product.variants.find(item => String(item.id) === String(entry.variant_id));
        if (Number(product.id) !== Number(entry.product_id) || !variant?.available) return null;
        return { entry, product, variant };
      } catch (error) {
        return null;
      }
    }
    progressiveState(tier) {
      const key = `${this.active.handle}:${tier.id}`;
      if (!this.progressiveSelections.has(key)) this.progressiveSelections.set(key, { initialized:false, slots:[] });
      return this.progressiveSelections.get(key);
    }
    progressiveImage(record, width = 160) {
      const override = this.rcOfferImage(record.product, 'selector_image');
      if (override) return override;
      const media = record.product.media?.find(item => String(item.id) === String(record.entry.media_id)) || record.product.media?.[0] || (record.product.featured_image ? { src:record.product.featured_image, width:1000, height:1000 } : null);
      return media ? this.image(media, record.product.title, width) : null;
    }
    rcOfferImage(product, purpose = 'offer_image') {
      if (this.room !== 'rc-garage') return null;
      const entry = this.bundleConfig?.offer_images?.[product.handle];
      if (!entry || Number(entry.product_id) !== Number(product.id) || !entry[purpose]) return null;
      const image = make('img', 'arcade-rc-clean-image');
      image.src = new URL(entry[purpose], new URL(this.dataset.bundleConfigUrl, location.href)).href;
      image.alt = product.title;
      image.decoding = 'async';
      if (entry.presentation_fit) image.style.objectFit = entry.presentation_fit;
      if (entry.presentation_position) image.style.objectPosition = entry.presentation_position;
      return image;
    }
    refreshRcSingleImage() {
      const image = this.rcOfferImage(this.active);
      if (image) this.querySelector('[data-single-image]').replaceChildren(image);
    }
    rcArtworkImage(source, className) {
      const image = make('img', className);
      image.src = source;
      image.alt = '';
      image.loading = 'eager';
      image.decoding = 'async';
      return image;
    }
    async configureProgressiveOffers(config) {
      this.bundleConfig = config;
      if (this.flyingSquad?.supports()) return this.flyingSquad.activate();
      if (this.rcFamily) { this.rcFamily.configure(config.supercar_family); await this.rcFamily.refresh(); return true; }
      this.refreshRcSingleImage();
      const host = this.querySelector('[data-progressive-offers]');
      if (!host) return false;
      const definition = this.progressiveDefinition(config);
      if (!definition) { host.hidden = true; host.replaceChildren(); return false; }
      this.querySelector('[data-bundle-offer]').hidden = true;
      await this.renderProgressiveOffers(definition);
      return true;
    }
    async renderProgressiveOffers(definition = this.progressiveDefinition()) {
      const host = this.querySelector('[data-progressive-offers]');
      if (!host || !definition) return;
      const revision = ++this.progressiveRevision, activeHandle = this.active.handle;
      const records = (await Promise.all((definition.pool.products || []).map(entry => this.selectorProduct(entry)))).filter(Boolean);
      let gift = null;
      const firstGift = definition.source.tiers?.map(tier => tier.gift).find(Boolean);
      if (firstGift && definition.engine.gifts?.[firstGift]) gift = await this.selectorProduct(definition.engine.gifts[firstGift]);
      if (revision !== this.progressiveRevision || activeHandle !== this.active.handle || !this.isConnected) return;
      const available = records.filter(record => record.variant.available);
      const companions = available.filter(record => record.entry.handle !== activeHandle);
      const labels = definition.engine.selector_labels || {};
      const fragment = document.createDocumentFragment();
      for (const tier of definition.source.tiers || []) {
        const paidSlotCount = this.room === 'rc-garage' ? tier.companion_count + 1 : tier.companion_count;
        if ((this.room === 'rc-garage' ? available.length : companions.length) < paidSlotCount) continue;
        const state = this.progressiveState(tier);
        if (!state.initialized) {
          state.slots = this.room === 'rc-garage'
            ? [activeHandle, ...companions.map(record => record.entry.handle)].slice(0, paidSlotCount)
            : companions.slice(0, tier.companion_count).map(record => record.entry.handle);
          state.initialized = true;
        }
        const selected = state.slots.slice(0, paidSlotCount);
        const selectedRecords = selected.map(handle => available.find(record => record.entry.handle === handle) || null);
        const invalid = selectedRecords.some(record => !record);
        const value = `selector:${tier.id}`;
        const card = make('section', `arcade-offer arcade-offer--progressive${tier.best_value ? ' arcade-offer--best-value' : ''}`);
        card.dataset.offerCard = '';
        card.dataset.progressiveTier = tier.id;
        const header = make('label', 'arcade-progressive-offer__header');
        const radio = make('input'); radio.type = 'radio'; radio.name = this.querySelector('[data-offer-radio]').name; radio.value = value; radio.dataset.offerRadio = ''; radio.checked = this.offer === value;
        radio.addEventListener('change', () => {
          if (this.busy || this.purchasing || radio.disabled) return;
          this.offerChoiceRevision++; this.offer = value; this.quantity(this.form.elements.quantity.value); this.syncControls();
        }, { signal:this.lifecycle.signal });
        const indicator = make('span', 'arcade-offer__radio'); indicator.setAttribute('aria-hidden', 'true');
        const copy = make('span', 'arcade-progressive-offer__copy');
        copy.append(make('strong', 'arcade-progressive-offer__title-art', tier.title), make('small', 'arcade-progressive-offer__banner', tier.subheading));
        const price = make('strong', 'arcade-progressive-offer__price', Number.isSafeInteger(tier.bundle_price) ? this.money(tier.bundle_price) : 'Price pending');
        if (!Number.isSafeInteger(tier.bundle_price)) price.dataset.pendingPrice = '';
        if (this.room === 'rc-garage') {
          header.classList.add(`arcade-rc-header--${tier.id}`);
          header.append(radio, indicator);
          if (tier.id === 'street-legends') header.append(this.rcArtworkImage(this.dataset.rcStreetHeaderUrl, 'arcade-rc-header__street-art'));
          else {
            header.append(this.rcArtworkImage(this.dataset.rcUltimateLeftUrl, 'arcade-rc-header__ultimate-left'));
            header.append(this.rcArtworkImage(this.dataset.rcUltimateTitleUrl, 'arcade-rc-header__ultimate-title'));
          }
          header.append(make('span', 'visually-hidden', `${tier.title}. ${tier.subheading}`));
        } else {
          header.append(radio, indicator, copy);
          if (tier.best_value) header.append(make('span', 'arcade-progressive-offer__badge', 'BEST VALUE'));
        }
        const body = make('div', 'arcade-progressive-offer__body'); body.dataset.progressiveBody = ''; body.hidden = !radio.checked;
        if (this.room !== 'rc-garage') { const priceDetails = make('div', 'arcade-progressive-offer__price-details'); priceDetails.append(price); body.append(priceDetails); }
        const activeRecord = { entry:{ media_id:this.games.get(activeHandle)?.heroId }, product:this.active, variant:this.variant };
        if (this.room !== 'rc-garage') {
          const locked = make('div', 'arcade-progressive-offer__locked');
          const activeImage = this.progressiveImage(activeRecord, 120);
          if (activeImage) locked.append(activeImage);
          const lockedCopy = make('span'); lockedCopy.append(make('small', '', labels.active || 'YOUR RIDE'), make('strong', '', this.active.title), make('em', '', `${this.money(this.variant.price)} · locked`)); locked.append(lockedCopy); body.append(locked);
        }
        const renderedSlotCount = this.room === 'rc-garage' ? paidSlotCount : tier.companion_count;
        for (let slot = 0; slot < renderedSlotCount; slot++) {
          const chosen = selectedRecords[slot];
          const selector = document.createElement('details'); selector.className = 'arcade-progressive-selector';
          const summary = document.createElement('summary');
          const slotLabel = this.room === 'rc-garage'
            ? `${labels.multi_companion || 'CHOOSE RIDE'} #${slot + 1}`
            : tier.companion_count === 1 ? (labels.single_companion || 'CHOOSE YOUR SECOND RIDE') : `${labels.multi_companion || 'CHOOSE RIDE'} #${slot + 2}`;
          const summaryCopy = make('span'); summaryCopy.append(make('small', '', slotLabel));
          if (chosen) {
            const selectedImage = this.progressiveImage(chosen, 90);
            if (selectedImage) summary.append(selectedImage);
            summaryCopy.append(make('strong', '', chosen.product.title), make('em', '', this.money(chosen.variant.price)));
          } else summaryCopy.append(make('strong', '', labels.placeholder || 'Choose an eligible companion'));
          summary.append(summaryCopy); selector.append(summary);
          const choices = make('div', 'arcade-progressive-selector__choices');
          available.filter(record => record.entry.handle !== chosen?.entry.handle).forEach(record => {
            const option = make('button', 'arcade-progressive-selector__choice'); option.type = 'button'; option.dataset.progressiveChoice = ''; option.dataset.tier = tier.id; option.dataset.slot = String(slot); option.dataset.handle = record.entry.handle;
            const image = this.progressiveImage(record, 90); if (image) option.append(image);
            const optionCopy = make('span'); optionCopy.append(make('strong', '', record.product.title), make('small', '', this.money(record.variant.price))); option.append(optionCopy); choices.append(option);
          });
          selector.append(choices); body.append(selector);
        }
        if (invalid) body.append(make('p', 'arcade-progressive-offer__error', labels.unavailable || 'A selected companion is unavailable. Choose another one to continue.'));
        if (this.room !== 'rc-garage') {
          const composition = make('div', 'arcade-progressive-offer__composition');
          [activeRecord, ...selectedRecords.filter(Boolean)].forEach(record => { const image = this.progressiveImage(record, 140); if (image) composition.append(image); });
          body.append(composition);
        }
        const giftDefinition = tier.gift && definition.engine.gifts?.[tier.gift];
        if (giftDefinition?.identity_verified && gift) {
          const giftStrip = make('div', 'arcade-progressive-offer__gift');
          const giftImage = this.progressiveImage(gift, this.room === 'rc-garage' ? 240 : 120);
          const giftCopy = make('span'); giftCopy.append(make('strong', '', 'FREE'), make('small', '', giftDefinition.label), make('b', '', this.room === 'rc-garage' ? 'A LEGEND ON US' : giftDefinition.title));
          if (this.room === 'rc-garage') {
            giftStrip.classList.add('arcade-rc-gift');
            const giftArt = this.rcArtworkImage(this.dataset.rcMustangMessageUrl, 'arcade-rc-gift__message');
            giftArt.setAttribute('aria-hidden', 'true');
            giftStrip.append(giftArt, make('span', 'visually-hidden', 'FREE. American Icon. A Legend on Us.'));
            const flag = this.rcArtworkImage(this.dataset.rcFlagAccentUrl, 'arcade-rc-gift__flag');
            flag.setAttribute('aria-hidden', 'true');
            giftStrip.append(flag);
            if (giftImage) giftStrip.append(giftImage);
            const giftCaption = make('em', 'arcade-rc-gift__caption');
            giftCaption.append(make('span', '', giftDefinition.title), make('span', '', 'Included with this bundle'));
            giftStrip.append(giftCaption);
          } else {
            if (giftImage) giftStrip.append(giftImage);
            if (!giftDefinition.commercially_enabled) giftCopy.append(make('em', '', 'Bundle preview only'));
            giftStrip.append(giftCopy);
          }
          body.append(giftStrip);
        }
        if (this.room === 'rc-garage') {
          const valuePanel = make('div', 'arcade-rc-value');
          const bundleValue = make('span'); bundleValue.append(make('small', '', 'BUNDLE PRICE'), price);
          const retail = make('span'); retail.append(make('small', '', Number.isSafeInteger(tier.retail_value) ? `${this.money(tier.retail_value).replace(/\.00$/, '')} RETAIL VALUE` : 'RETAIL VALUE'), make('del', '', Number.isSafeInteger(tier.retail_value) ? this.money(tier.retail_value).replace(/\.00$/, '') : 'Pending'));
          const savings = make('span'); savings.append(make('small', '', 'YOU SAVE'), make('strong', '', Number.isSafeInteger(tier.savings) ? this.money(tier.savings) : 'Pending'));
          const tagline = this.rcArtworkImage(this.dataset.rcPriceTaglineUrl, 'arcade-rc-value__tagline');
          tagline.setAttribute('aria-hidden', 'true');
          valuePanel.append(bundleValue, retail, savings, tagline, make('span', 'visually-hidden', 'More Cars. Bigger Adventures.'));
          body.append(valuePanel);
        } else {
          const benefits = make('ul', 'arcade-progressive-offer__benefits'); benefits.append(...(tier.benefits || []).map(text => make('li', '', text))); body.append(benefits);
        }
        card.append(header, body); card.toggleAttribute('data-selected', radio.checked); card.toggleAttribute('data-expanded', radio.checked); fragment.append(card);
      }
      host.replaceChildren(fragment); host.hidden = !host.children.length;
      this.syncControls();
    }
    async chooseProgressiveCompanion(tierId, slot, handle) {
      const definition = this.progressiveDefinition(), tier = definition?.source.tiers?.find(item => item.id === tierId);
      const entry = definition?.pool.products?.find(item => item.handle === handle);
      if (!definition || !tier || !entry || (this.room !== 'rc-garage' && handle === this.active.handle)) return;
      const candidate = await this.selectorProduct(entry);
      if (!candidate) return;
      const state = this.progressiveState(tier);
      if (!state.initialized) { state.slots = Array(this.room === 'rc-garage' ? tier.companion_count + 1 : tier.companion_count).fill(null); state.initialized = true; }
      const slotIndex = Number(slot), previous = state.slots[slotIndex], occupied = state.slots.indexOf(handle);
      state.slots[slotIndex] = handle;
      if (occupied !== -1 && occupied !== slotIndex) state.slots[occupied] = previous;
      this.offer = `selector:${tierId}`;
      await this.renderProgressiveOffers(definition);
      this.syncControls();
    }
    bundlePrice() {
      const price = this.activeProgressiveTier()?.bundle_price ?? this.bundle?.pricing?.price ?? this.bundleConfig?.bundle_price;
      return Number.isSafeInteger(price) && price > 0 ? this.money(price) : (this.offerPresentation?.pending_price || this.t('variant_unavailable'));
    }
    bundleGuard() { return this.offerPresentation?.guard_copy || this.t('bundle_guard'); }
    money(cents) {
      const formats = { amount:[2,',','.'], amount_no_decimals:[0,',','.'], amount_with_comma_separator:[2,'.',','], amount_no_decimals_with_comma_separator:[0,'.',','], amount_with_space_separator:[2,' ',','], amount_no_decimals_with_space_separator:[0,' ',','], amount_with_apostrophe_separator:[2,"'",'.'] };
      const match = this.moneyPattern.match(/\{\{\s*(\w+)\s*\}\}/);
      if (!match || !formats[match[1]]) return new Intl.NumberFormat(document.documentElement.lang, { style:'currency', currency:this.dataset.currency }).format(cents / 100);
      const [precision, grouping, decimal] = formats[match[1]];
      const [whole, fraction] = (cents / 100).toFixed(precision).split('.');
      const plain = make('span');
      plain.innerHTML = this.moneyPattern.replace(match[0], whole.replace(/\B(?=(\d{3})+(?!\d))/g, grouping) + (fraction ? decimal + fraction : ''));
      return plain.textContent;
    }
    handleClick(event) {
      const button = event.target.closest('button');
      if (!button || button.disabled) return;
      if (button.hasAttribute('data-progressive-choice')) {
        event.preventDefault();
        this.chooseProgressiveCompanion(button.dataset.tier, Number(button.dataset.slot), button.dataset.handle);
        return;
      }
      if (button.hasAttribute('data-product-handle')) {
        if (this.room === 'arcade' && !this.arcadeRosterReady) return;
        if (this.flyingSquad?.campaignDrawer && this.flyingSquad.state) {
          const record = this.flyingSquad.records.find(r => r.handle === button.dataset.productHandle);
          if (record) this.flyingSquad.view(record.variant_id);
          return;
        }
        this.activate(button.dataset.productHandle, { history:'push' }).then(ok => {
          if (ok && button.hasAttribute('data-return-top')) this.querySelector('[data-pdp]').scrollIntoView({ behavior:matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block:'start' });
        });
      }
      if (button.hasAttribute('data-quantity-delta')) this.quantity(Number(this.form.elements.quantity.value) + Number(button.dataset.quantityDelta));
      if (button.hasAttribute('data-media-index')) this.navigateMedia(Number(button.dataset.mediaIndex));
      if (button.hasAttribute('data-media-step')) this.navigateMedia(this.mediaIndex() + Number(button.dataset.mediaStep));
    }
    quantity(value) {
      if (this.flyingSquad?.quantity(value)) return;
      const rule = this.isGuardedOffer() ? { min:1, increment:1, max:this.bundle?.available ? Math.min(...this.bundle.components.map(c => Math.min(c.audited_inventory ?? 9999, c.variant.quantity_rule?.max || 9999))) : 1 } : this.variant?.quantity_rule;
      const min = rule?.min || 1, step = rule?.increment || 1, max = rule?.max || 9999;
      this.form.elements.quantity.value = Math.max(min, Math.min(max, min + Math.floor((Number(value || min) - min) / step) * step));
      this.syncControls();
    }
    async refreshBundle({ preserveOffer = false } = {}) {
      if (this.flyingSquad?.supports()) { this.bundleRevision++; this.bundle = null; this.bundlePending = false; this.flyingSquad.activate(); return; }
      this.flyingSquad?.deactivate();
      const revision = ++this.bundleRevision, handle = this.active.handle;
      const previousOffer = this.offer, choiceRevision = this.offerChoiceRevision;
      this.bundle = null; this.bundlePending = true;
      if (!preserveOffer && !this.rcFamily) this.offer = 'single';
      this.syncControls();
      const config = await this.bundleConfigPromise;
      if (revision !== this.bundleRevision || !this.isConnected) return;
      this.bundleConfig = config;
      if (this.rcFamily) { this.rcFamily.configure(config?.supercar_family); await this.rcFamily.refresh(); return; }
      this.refreshRcSingleImage();
      const playsetAnchor = this.playsetAnchorVariant(config, handle);
      if (playsetAnchor && String(this.variant?.id) !== String(playsetAnchor)) {
        this.selectVariant(playsetAnchor);
        this.writeRoute(handle, 'replace', playsetAnchor);
      }
      const progressive = this.progressiveDefinition(config);
      if (progressive) {
        this.bundlePending = false;
        this.querySelector('[data-bundle-offer]').hidden = true;
        await this.renderProgressiveOffers(progressive);
        if (revision !== this.bundleRevision || handle !== this.active.handle || !this.isConnected) return;
        this.syncControls();
        return;
      }
      const progressiveHost = this.querySelector('[data-progressive-offers]');
      if (progressiveHost) { progressiveHost.hidden = true; progressiveHost.replaceChildren(); }
      const chosenOffer = preserveOffer ? previousOffer : (this.flyingSquad?.campaignDrawer && this.flyingSquad.state?.offerMode === 'custom' ? 'single' : config?.presentation?.default_offer || 'bundle');
      const definition = config?.bundles?.[handle];
      const bundleEnabled = definition && (definition.bundle_enabled ?? definition.enabled) === true;
      const configured = config && ((definition?.eligible && bundleEnabled) || config.presentation?.show_unconfigured) && this.dataset.currency === 'USD';
      this.querySelector('[data-bundle-offer]').hidden = !configured;
      if (!configured) { this.bundlePending = false; this.offer = 'single'; this.syncControls(); return; }
      // Availability is fetched afresh, separately from the product-switching cache.
      const bundle = await window.ArcadeBundles.resolve(config, handle, async componentHandle => {
        if (!this.games.has(componentHandle) && componentHandle !== config.free_gift?.handle && !config.accessory_handles?.includes(componentHandle)) throw new Error('Unreviewed bundle component');
        const response = await fetch(`${this.base}products/${encodeURIComponent(componentHandle)}.js`, { signal:this.lifecycle.signal, headers:{ Accept:'application/json' }, cache:'no-store' });
        if (!response.ok) throw new Error('Bundle component unavailable');
        return response.json();
      });
      if (revision !== this.bundleRevision || handle !== this.active.handle || !this.isConnected) return;
      if (bundle.available && !bundle.gift_replaces_source && !bundle.components.some(c => c.handle === handle && c.variant_id === this.variant?.id)) { bundle.available = false; bundle.reason = 'variant_mismatch'; }
      this.bundle = bundle; this.bundlePending = false; this.bundleCheckedAt = Date.now();
      this.offer = bundle.available ? (choiceRevision === this.offerChoiceRevision ? chosenOffer : this.offer) : 'single';
      const images = this.querySelector('[data-bundle-images]'); images.replaceChildren();
      const bonusImage = this.querySelector('[data-bundle-bonus-image]'); bonusImage.replaceChildren();
      const displayComponents = bundle.components.length ? bundle.components : (['drones', 'flying-figures', 'playsets', 'rc-garage'].includes(this.room) ? [{ handle, category:this.room === 'drones' ? 'Drone' : this.room === 'flying-figures' ? 'Flying Figures' : this.room === 'playsets' ? 'Playsets' : 'RC Garage', product:this.active, media_id:this.games.get(handle)?.heroId }] : []);
      for (const component of displayComponents) {
        const game = this.games.get(component.handle);
        const artwork = this.bundleArtwork[component.handle];
        const media = artwork || component.product.media.find(m => String(m.id) === String(component.media_id || game?.heroId));
        if (!media) continue;
        const visual = make('span', 'arcade-offer__game');
        visual.dataset.category = component.category;
        if (artwork) visual.dataset.cleanArtwork = '';
        else visual.dataset.mediaId = media.id;
        visual.dataset.componentHandle = component.handle;
        const paidGame = !config.free_gift || component.variant_id !== config.free_gift.variant_id;
        const image = this.image(media, component.product.title, 600, false, paidGame);
        visual.append(image);
        if (paidGame && ['flying-figures', 'playsets', 'rc-garage'].includes(this.room)) this.frameBundleImage(image, visual);
        (paidGame ? images : bonusImage).append(visual);
      }
      this.prioritizeArcadeBundleVisuals();
      images.dataset.count = images.children.length;
      const values = window.ArcadeBundles.pricing(config, config.bundles?.[handle]);
      this.querySelector('[data-bundle-price]').textContent = this.bundlePrice();
      this.querySelector('[data-bundle-price]').toggleAttribute('data-pending-price', !values.valid);
      this.querySelector('.arcade-offer__retail').hidden = !values.valid;
      this.querySelector('.arcade-offer__saving').hidden = !values.valid;
      this.querySelector('.arcade-offer__bonus').hidden = !config.free_gift;
      this.querySelector('[data-bundle-retail]').textContent = values.valid ? this.money(values.retail).replace(/\.00$/, '') : '';
      this.querySelector('[data-bundle-saving]').textContent = values.valid ? this.money(values.savings) : '';
      const mapping = this.querySelector('[data-bundle-mapping]');
      mapping.hidden = !['drones', 'flying-figures', 'playsets', 'rc-garage'].includes(this.room);
      mapping.textContent = bundle.available ? bundle.components.filter(c => c.handle !== handle).map(c => c.category).join(' + ') : (config.presentation?.unconfigured_copy || '');
      this.quantity(this.form.elements.quantity.value);
      this.dispatchEvent(new CustomEvent('arcade:bundle-state', { detail:{ handle, available:bundle.available, reason:bundle.reason, components:bundle.components.map(c => ({ handle:c.handle, variant_id:c.variant_id, category:c.category, fallback_used:c.fallback_used })), diagnostics:bundle.diagnostics } }));
    }
    prioritizeArcadeBundleVisuals() {
      if (this.room !== 'arcade') return;
      const images = this.querySelector('[data-bundle-images]');
      const paidCategories = ['Air Hockey', 'Foosball', 'Pool'];
      const activeCategory = this.games.get(this.active.handle)?.category;
      // Bowling keeps the approved Pool-led default; its gift stays in the FREE banner.
      const foreground = paidCategories.includes(activeCategory) ? activeCategory : 'Pool';
      images.dataset.categoryPriority = foreground;
      let support = 0;
      for (const visual of images.children) {
        if (!paidCategories.includes(visual.dataset.category)) continue;
        visual.dataset.bundleSlot = visual.dataset.category === foreground ? 'foreground' : `support-${++support}`;
      }
    }
    bundleComposition(quantity = Number(this.form.elements.quantity.value)) {
      return window.ArcadeBundles.compose(this.bundle, quantity);
    }
    frameBundleImage(image, frame) {
      frame.style.setProperty('--media-ratio', image.width / image.height);
      image.loading = 'eager';
      const key = image.src;
      if (!this.bundleImageFrames.has(key)) this.bundleImageFrames.set(key, image.decode().then(() => {
        // Inspect a small thumbnail, without changing the original image or its Shopify source.
        const scale = Math.min(1, 192 / Math.max(image.naturalWidth, image.naturalHeight));
        const canvas = document.createElement('canvas');
        const w = canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
        const h = canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
        const ctx = canvas.getContext('2d', { willReadFrequently:true });
        ctx.drawImage(image, 0, 0, w, h);
        const pixels = ctx.getImageData(0, 0, w, h).data, mask = new Uint8Array(w * h);
        for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
          const p = (y * w + x) * 4;
          if (pixels[p + 3] < 32 || Math.min(pixels[p], pixels[p + 1], pixels[p + 2]) > 232) continue;
          // Join nearby edges so a table, its legs and controls remain a single object.
          for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
            if (x + dx >= 0 && x + dx < w && y + dy >= 0 && y + dy < h) mask[(y + dy) * w + x + dx] = 1;
          }
        }
        const queue = new Int32Array(w * h), groups = [];
        for (let i = 0; i < mask.length; i++) {
          if (!mask[i]) continue;
          let head = 0, tail = 1, left = w, right = 0, top = h, bottom = 0;
          queue[0] = i; mask[i] = 0;
          while (head < tail) {
            const n = queue[head++], x = n % w, y = Math.floor(n / w);
            left = Math.min(left, x); right = Math.max(right, x); top = Math.min(top, y); bottom = Math.max(bottom, y);
            for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
              const nx = x + dx, ny = y + dy, next = ny * w + nx;
              if (nx >= 0 && nx < w && ny >= 0 && ny < h && mask[next]) { mask[next] = 0; queue[tail++] = next; }
            }
          }
          groups.push({ left, right, top, bottom, area:tail });
        }
        groups.sort((a, b) => b.area - a.area);
        const main = groups[0];
        if (!main) return null;
        const confident = main.right - main.left > w * .45 && main.bottom - main.top > h * .2;
        // Keep adjacent accessories; omit detached logo headers above a confidently identified game.
        const kept = groups.filter(g => g.area >= 4 && (!confident || (g.bottom >= main.top - h * .02 && g.top <= main.bottom + h * .04)));
        const pad = Math.max(2, Math.ceil(Math.max(w, h) * .018));
        const left = Math.max(0, Math.min(...kept.map(g => g.left)) - pad) / w;
        const top = Math.max(0, Math.min(...kept.map(g => g.top)) - pad) / h;
        const right = Math.min(w, Math.max(...kept.map(g => g.right)) + pad + 1) / w;
        const bottom = Math.min(h, Math.max(...kept.map(g => g.bottom)) + pad + 1) / h;
        const cw = (right - left) * image.naturalWidth, ch = (bottom - top) * image.naturalHeight;
        return { ratio:cw / ch, width:100 / (right - left), left:-left * image.naturalWidth / cw * 100, top:-top * image.naturalHeight / cw * 100 };
      }).catch(() => null));
      this.bundleImageFrames.get(key).then(bounds => {
        if (bounds) {
          frame.style.setProperty('--media-ratio', bounds.ratio);
          for (const name of ['width', 'left', 'top']) frame.style.setProperty(`--media-${name}`, `${bounds[name]}%`);
        }
        frame.dataset.bundleFramed = bounds ? 'content' : 'contain';
      });
    }
    syncControls() {
      if (this.flyingSquad?.campaignDrawer && this.blocked) {
        this.querySelectorAll('[data-atc],[data-checkout]').forEach(button => { button.disabled = true; });
        return;
      }
      if (this.flyingSquad?.handles()) { this.flyingSquad.sync(); return; }
      const locked = !!(this.busy || this.purchasing || (this.room === 'arcade' && !this.arcadeRosterReady)), available = !!this.variant?.available && !this.blocked;
      const bundled = this.isGuardedOffer();
      const purchaseGuarded = bundled && !window.HobbytronCampaignBundles?.selection(this);
      this.querySelector('[data-atc]').disabled = locked || !available || purchaseGuarded;
      this.querySelector('[data-checkout]').disabled = locked || !available || purchaseGuarded;
      this.querySelector('[data-atc-label]').textContent = this.busy ? this.t('loading') : available ? this.t('add') : this.t('sold_out');
      this.querySelector('[data-checkout-label]').textContent = this.purchasing && this.checkout ? this.t('adding') : this.t('buy');
      const availability = this.querySelector('[data-availability]');
      availability.textContent = this.busy ? this.t('loading') : available ? this.t('in_stock') : this.t('sold_out');
      availability.toggleAttribute('data-sold-out', !available);
      this.form.elements.quantity.disabled = locked || !available;
      this.querySelectorAll('[data-quantity-delta]').forEach(button => { button.disabled = locked || !available || (button.dataset.quantityDelta === '-1' && Number(this.form.elements.quantity.value) <= (this.variant?.quantity_rule?.min || 1)); });
      this.querySelector('[data-variant-select]').disabled = locked;
      for (const radio of this.querySelectorAll('[data-offer-radio]')) {
        radio.checked = this.offer === radio.value;
        radio.disabled = locked || (radio.value === 'bundle' && !this.bundle?.available);
        const card = radio.closest('[data-offer-card]') || radio.closest('.arcade-offer');
        card?.toggleAttribute('data-selected', radio.checked);
        // The RC family has two purchase modes and permanently visible bundle details.
        if (!this.rcFamily) {
          card?.toggleAttribute('data-expanded', radio.checked && this.isProgressiveOffer(radio.value));
          const body = card?.querySelector('[data-progressive-body]');
          if (body) body.hidden = !radio.checked;
        }
      }
      this.querySelector('[data-bundle-offer]').toggleAttribute('data-unavailable', !this.bundle?.available);
      const notice = this.querySelector('[data-bundle-notice]');
      notice.textContent = this.bundlePending ? this.t('bundle_pending') : purchaseGuarded ? this.bundleGuard() : this.bundle && !this.bundle.available ? (this.offerPresentation?.unavailable_copy || this.t('bundle_unavailable')) : '';
      notice.hidden = !notice.textContent;
      const quantityRule = bundled ? { min:1, increment:1 } : this.variant?.quantity_rule;
      this.form.elements.quantity.min = quantityRule?.min || 1;
      this.form.elements.quantity.step = quantityRule?.increment || 1;
      if (bundled) this.form.elements.quantity.removeAttribute('max');
      this.querySelectorAll('[data-product-handle]').forEach(button => { button.disabled = !!this.purchasing; });
    }
    selectVariant(id) {
      this.variant = this.active.variants.find(item => String(item.id) === String(id));
      this.form.elements.id.value = this.variant?.id || '';
      this.querySelector('[data-price]').textContent = this.variant ? this.money(this.variant.price) : this.t('variant_unavailable');
      this.querySelector('[data-variant-select]').value = String(this.variant?.id || '');
      this.form.elements.quantity.min = this.variant?.quantity_rule?.min || 1;
      this.form.elements.quantity.step = this.variant?.quantity_rule?.increment || 1;
      if (this.variant?.quantity_rule?.max) this.form.elements.quantity.max = this.variant.quantity_rule.max;
      else this.form.elements.quantity.removeAttribute('max');
      this.querySelector('arcade-purchase-form')?.handleErrorMessage?.();
      this.quantity(this.form.elements.quantity.value);
      const variantMedia = this.variant?.featured_media?.id || this.variant?.featured_image?.id;
      const index = this.galleryItems?.findIndex(media => String(media.id) === String(variantMedia));
      if (index >= 0) this.navigateMedia(index, 'instant');
      this.rcFamily?.syncPrimary();
    }
    writeRoute(handle, method, variantId) {
      if (this.flyingSquad?.master || this.flyingSquad?.campaignDrawer && this.flyingSquad.state) { this.flyingSquad.writeRoute(method); return; }
      const url = new URL(location.href);
      url.searchParams.set('product', handle);
      if (variantId) url.searchParams.set('variant', variantId);
      else url.searchParams.delete('variant');
      if (url.href !== location.href) history[`${method}State`](history.state, '', url);
    }
    async restoreRoute() {
      if (this.purchasing) { this.routePending = true; return; }
      if (this.flyingSquad?.master || this.flyingSquad?.campaignDrawer && this.flyingSquad.state) { return this.flyingSquad.restoreRoute(); }
      if (this.room === 'arcade') return this.restoreArcadeRoute();
      if ((this.room === 'rc-garage' && !this.rcFamily) || (this.room === 'playsets' && !this.hasAttribute('data-playsets-master'))) return this.restoreScopedRoomRoute();
      const url = new URL(location.href), requested = url.searchParams.get('product');
      if (this.rcFamily && requested !== null && (requested !== this.dataset.defaultHandle || url.searchParams.getAll('product').length !== 1)) {
        await this.bundleConfigPromise;
        if (!this.isConnected) return;
        this.showUnavailableRoute();
        this.removeAttribute('data-entry-pending');
        return;
      }
      const handle = !this.rcFamily && this.games.has(requested) ? requested : this.dataset.defaultHandle;
      if (requested !== null && (requested !== handle || url.searchParams.getAll('product').length > 1)) this.writeRoute(handle, 'replace');
      const promise = this.activate(handle, { variantId:url.searchParams.get('variant') });
      const request = this.requestId;
      const ok = await promise;
      if (request !== this.requestId || !this.isConnected) return;
      if (!ok && handle !== this.dataset.defaultHandle) {
        const fallback = await this.activate(this.dataset.defaultHandle);
        if (fallback) this.writeRoute(this.active.handle, 'replace');
      }
      this.removeAttribute('data-entry-pending');
    }
    async restoreArcadeRoute() {
      const revision = (this.routeRevision || 0) + 1;
      this.routeRevision = revision;
      const config = await this.bundleConfigPromise;
      if (revision !== this.routeRevision || !this.isConnected) return;
      const url = new URL(location.href), requested = url.searchParams.get('product');
      if (!config || !this.arcadeRosterReady || (requested !== null && (!this.games.has(requested) || url.searchParams.getAll('product').length !== 1))) {
        this.showUnavailableRoute();
        this.removeAttribute('data-entry-pending');
        return;
      }
      const handle = requested || this.dataset.defaultHandle;
      if (!this.games.has(handle)) {
        this.showUnavailableRoute();
        this.removeAttribute('data-entry-pending');
        return;
      }
      const ok = await this.activate(handle, { variantId:url.searchParams.get('variant') });
      if (revision !== this.routeRevision || !this.isConnected) return;
      if (!ok) this.showUnavailableRoute();
      this.removeAttribute('data-entry-pending');
    }
    async restoreScopedRoomRoute() {
      const revision = (this.routeRevision || 0) + 1;
      this.routeRevision = revision;
      // The existing RC family redirect must inspect the original requested alias
      // before the generic room can normalize an absent catalog entry to its default.
      if (this.room === 'rc-garage') await this.bundleConfigPromise;
      if (revision !== this.routeRevision || !this.isConnected || this.routeRedirectPending) return;
      const url = new URL(location.href), requested = url.searchParams.get('product');
      if (requested !== null && (!this.games.has(requested) || url.searchParams.getAll('product').length !== 1)) {
        this.showUnavailableRoute();
        this.removeAttribute('data-entry-pending');
        return;
      }
      const handle = this.games.has(requested) ? requested : this.dataset.defaultHandle;
      const promise = this.activate(handle, { variantId:url.searchParams.get('variant') });
      const request = this.requestId;
      const ok = await promise;
      if (revision !== this.routeRevision || request !== this.requestId || !this.isConnected) return;
      if (!ok) this.showUnavailableRoute();
      this.removeAttribute('data-entry-pending');
    }
    showUnavailableRoute() {
      this.querySelector('[data-unavailable-route]')?.remove();
      this.blocked = true;
      this.variant = null;
      this.form.elements.id.value = '';
      const productId = this.form.querySelector('[name="product-id"]');
      if (productId) productId.value = '';
      this.querySelector('[data-title]').textContent = this.t('unavailable');
      this.querySelector('[data-description]').textContent = '';
      this.querySelector('[data-details-product]').textContent = '';
      this.querySelector('[data-price]').textContent = '';
      this.querySelector('[data-gallery]').replaceChildren(make('div', 'arcade-gallery__slide ht-arcade__slide', this.t('unavailable')));
      this.querySelector('[data-status]').textContent = this.t('unavailable');
      this.syncControls();
      // A failed audited identity must not display default-product content.
      for (const child of this.children) {
        if (child.tagName === 'HEADER' || child.tagName === 'SCRIPT' || child.tagName === 'NOSCRIPT' || child.hidden) continue;
        child.hidden = true;
        child.dataset.routeUnavailableHidden = '';
      }
      const notice = make('p', 'arcade-message', this.t('unavailable'));
      notice.dataset.unavailableRoute = '';
      notice.setAttribute('role', 'alert');
      notice.style.cssText = 'margin:32px auto;padding:24px;text-align:center;max-width:600px';
      this.querySelector('[data-pdp]').before(notice);
    }
    loadProduct(handle) {
      const cached = this.products.get(handle);
      if (cached && Date.now() - cached.loaded < 120000) return Promise.resolve(cached.product);
      if (this.productRequests.has(handle)) return this.productRequests.get(handle).promise;
      const controller = new AbortController();
      const promise = fetch(`${this.base}products/${encodeURIComponent(handle)}.js`, { signal:controller.signal, headers:{ Accept:'application/json' }, cache:'no-store' })
        .then(async response => {
          if (!response.ok) throw new Error(this.t('load_error'));
          const product = await response.json();
          if (product.id !== this.games.get(handle)?.id || product.handle !== handle || !Array.isArray(product.variants) || !product.variants.length) throw new Error(this.t('unavailable'));
          if (product.variants.some(variant => !Number.isSafeInteger(variant.price) || variant.price < this.minimumPrice)) throw new Error(this.t('price_excluded'));
          this.products.set(handle, { product, loaded:Date.now() });
          return product;
        }).finally(() => this.productRequests.delete(handle));
      this.productRequests.set(handle, { controller, promise });
      return promise;
    }
    async activate(handle, options = {}) {
      if (this.purchasing || !this.games.has(handle)) return false;
      const request = ++this.requestId;
      this.busy = true;
      this.querySelector('[data-pdp]').setAttribute('aria-busy', 'true');
      this.querySelector('[data-status]').textContent = '';
      this.syncControls();
      try {
        const product = await this.loadProduct(handle);
        if (request !== this.requestId || !this.isConnected) return false;
        this.blocked = false;
        this.querySelector('[data-unavailable-route]')?.remove();
        for (const child of this.querySelectorAll(':scope > [data-route-unavailable-hidden]')) {
          child.hidden = false;
          delete child.dataset.routeUnavailableHidden;
        }
        this.renderProduct(product, options.variantId);
        if (options.history) this.writeRoute(handle, options.history);
        document.title = `${product.title} | ${this.t('identity')}`;
        this.dispatchEvent(new CustomEvent('arcade:product-change', { bubbles:true, detail:{ product } }));
        return true;
      } catch (error) {
        if (request === this.requestId && error.name !== 'AbortError') {
          this.querySelector('[data-status]').textContent = error.message;
          if (handle === this.active.handle) this.blocked = true;
        }
        return false;
      } finally {
        if (request === this.requestId) {
          this.busy = false;
          this.querySelector('[data-pdp]').setAttribute('aria-busy', 'false');
          this.removeAttribute('data-entry-pending');
          this.syncControls();
        }
      }
    }
    renderProduct(product, variantId) {
      if (this.flyingSquad?.master) { this.flyingSquad.initialize(); return; }
      const changed = this.active?.id !== product.id;
      this.active = product;
      this.prioritizeArcadeBundleVisuals();
      const game = this.games.get(product.handle);
      game.product = product;
      this.renderLineup(game.category);
      this.renderDiscoverMore();
      this.querySelector('[data-title]').textContent = product.title;
      this.querySelector('[data-category]').textContent = game.category;
      this.querySelector('[data-details-product]').textContent = product.title;
      this.rebuildMedia();
      const singleImage = this.querySelector('[data-single-image]'); singleImage.replaceChildren();
      if (this.galleryItems[0]) singleImage.append(this.image(this.galleryItems[0], product.title, 360));
      this.refreshRcSingleImage();
      const select = this.querySelector('[data-variant-select]');
      select.replaceChildren(...product.variants.map(variant => {
        const option = make('option', '', `${variant.title} — ${this.money(variant.price)}${variant.available ? '' : ` — ${this.t('sold_out')}`}`);
        option.value = variant.id; option.disabled = !variant.available; return option;
      }));
      const variantWrap = this.querySelector('[data-variant-wrap]');
      if (variantWrap) variantWrap.hidden = product.variants.length === 1 && product.variants[0].title === 'Default Title';
      if (changed) this.form.elements.quantity.value = 1;
      this.form.querySelector('[name="product-id"]')?.setAttribute('value', product.id);
      this.renderFacts(product, game);
      this.selectVariant((product.variants.find(variant => String(variant.id) === String(variantId || (this.rcFamily ? this.dataset.familyDefaultVariant : ''))) || product.variants.find(variant => variant.available) || product.variants[0]).id);
      this.refreshBundle();
      this.querySelectorAll('[data-product-handle]').forEach(button => {
        button.setAttribute('aria-pressed', String(button.dataset.productHandle === product.handle));
        if (button.dataset.productHandle === product.handle) {
          const price = button.querySelector('[data-card-price]'), state = button.querySelector('[data-card-state]');
          if (price) price.textContent = this.money(Math.min(...product.variants.map(variant => variant.price)));
          if (state) state.textContent = product.available ? '' : this.t('sold_out');
        }
      });
    }
    renderLineup(category) {
      if (this.rcFamily) return;
      const headings = { 'Air Hockey':'more_air', Bowling:'more_bowling', Foosball:'more_foosball', Pool:'more_pool' };
      this.querySelector('[data-lineup-heading]').textContent = ['drones', 'flying-figures', 'playsets', 'rc-garage'].includes(this.room) ? `MORE ${category.toUpperCase()}` : this.t(headings[category]);
      const lineup = this.querySelector('[data-lineup]');
      const eligible = [...this.games.values()].filter(game => game.category === category && game.product?.variants?.length && game.product.variants.every(variant => Number.isSafeInteger(variant.price) && variant.price >= this.minimumPrice));
      const key = eligible.map(game => game.id).join(',');
      if (this.lineupKey === key) return;
      this.lineupKey = key;
      lineup.replaceChildren(...eligible.map(game => {
        const product = game.product;
        const button = make('button', 'arcade-card ht-arcade__game'); button.type = 'button'; button.dataset.productHandle = game.handle;
        const visual = make('span', 'arcade-card__image ht-arcade__game-image');
        const media = product.media.find(item => String(item.id) === game.heroId);
        if (media) visual.append(this.image(media, product.title, 500));
        const price = make('span', 'arcade-card__price ht-arcade__game-price', this.money(Math.min(...product.variants.map(variant => variant.price)))); price.dataset.cardPrice = '';
        const state = make('span', 'arcade-card__sold ht-arcade__game-state', product.available ? '' : this.t('sold_out')); state.dataset.cardState = '';
        visual.append(state);
        button.append(visual, make('span', 'arcade-card__title', product.title), price);
        return button;
      }));
      lineup.scrollLeft = 0;
    }
    merchandiseHref(entry) {
      const url = new URL(entry.route || `${this.base}products/${encodeURIComponent(entry.handle)}`, location.origin);
      ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'fbclid', 'gclid'].forEach(key => {
        const value = new URL(location.href).searchParams.get(key);
        if (value && !url.searchParams.has(key)) url.searchParams.set(key, value);
      });
      return `${url.pathname}${url.search}${url.hash}`;
    }
    async loadMerchandise(entry) {
      const cached = this.products.get(`merchandise:${entry.handle}`);
      if (cached && Date.now() - cached.loaded < 120000) return cached.product;
      try {
        const response = await fetch(`${this.base}products/${encodeURIComponent(entry.handle)}.js`, { signal:this.lifecycle.signal, headers:{ Accept:'application/json' }, cache:'no-store' });
        if (!response.ok) return null;
        const product = await response.json();
        if (product.handle !== entry.handle || Number(product.id) !== Number(entry.product_id) || !product.available || !product.variants?.some(variant => variant.available && Number.isSafeInteger(variant.price) && variant.price > 0)) return null;
        this.products.set(`merchandise:${entry.handle}`, { product, loaded:Date.now() });
        return product;
      } catch (error) {
        if (error.name !== 'AbortError') return null;
        return null;
      }
    }
    discoverCard(entry, product) {
      const card = make('a', 'arcade-card arcade-discover__card');
      card.href = this.merchandiseHref(entry);
      const visual = make('span', 'arcade-card__image ht-arcade__game-image');
      const media = product.media?.find(item => String(item.id) === String(entry.hero_media_id)) || product.media?.[0] || (product.featured_image ? { src:product.featured_image, width:1000, height:1000 } : null);
      if (media) visual.append(this.image(media, product.title, 500));
      const available = product.variants.filter(variant => variant.available && Number.isSafeInteger(variant.price));
      const price = make('span', 'arcade-card__price ht-arcade__game-price', this.money(Math.min(...available.map(variant => variant.price))));
      card.append(visual, make('span', 'arcade-card__title', product.title), price);
      return card;
    }
    async renderDiscoverMore() {
      if (this.rcFamily) return;
      const module = this.querySelector('[data-discover-more]');
      const rail = this.querySelector('[data-discover-rail]');
      const priority = this.merchandisingPriority;
      if (!module || !rail || !priority || !this.active?.handle) return;
      const revision = ++this.discoverRevision;
      const activeIsSpiderMan = (priority.active_handles || []).includes(this.active.handle);
      const configuredProducts = new Map((priority.products || []).map(entry => [entry.handle, entry]));
      const configured = (priority.ordered_handles || priority.products?.map(entry => entry.handle) || [])
        .map(handle => configuredProducts.get(handle))
        .filter(entry => entry && entry.handle !== this.active.handle);
      const entries = activeIsSpiderMan ? configured : [...configured];
      if (!activeIsSpiderMan) {
        [...this.games.values()]
          .filter(game => game.handle !== this.active.handle && game.category === this.games.get(this.active.handle)?.category && game.product?.available)
          .forEach(game => entries.push({ handle:game.handle, product_id:game.id, hero_media_id:game.heroId, route:`${location.pathname}?view=${encodeURIComponent(new URL(location.href).searchParams.get('view') || '')}&product=${encodeURIComponent(game.handle)}`, product:game.product }));
      }
      const unique = [...new Map(entries.map(entry => [entry.handle, entry])).values()].slice(0, priority.max_cards || 4);
      const results = await Promise.all(unique.map(async entry => ({ entry, product:entry.product || await this.loadMerchandise(entry) })));
      if (revision !== this.discoverRevision || !this.isConnected) return;
      const visible = results.filter(result => result.product);
      rail.replaceChildren(...visible.map(result => this.discoverCard(result.entry, result.product)));
      this.querySelector('[data-discover-heading]').textContent = activeIsSpiderMan ? priority.heading : (priority.fallback_heading || 'DISCOVER MORE');
      module.hidden = !visible.length;
    }
    renderFacts(product, game) {
      const facts = productFacts(product.description);
      if (['drones', 'flying-figures', 'playsets', 'rc-garage'].includes(this.room)) {
        const body = new DOMParser().parseFromString(product.description || '', 'text/html').body;
        if (!facts.features.length) facts.features = [...body.querySelectorAll('p')].map(textOf).filter(text => text.length > 60 && safeFact(text)).slice(0, 2);
      }
      const allFacts = [...facts.features, ...facts.includes, ...facts.specs].join(' ');
      const chips = [];
      let statement = facts.features[0] || '';
      if (game.category === 'Air Hockey') {
        if (/electronic/i.test(product.title)) chips.push(this.t('chip_air'));
        if (/tabletop/i.test(product.title)) chips.push(this.t('chip_table'));
        if (/players:\s*2\b/i.test(allFacts)) chips.push(this.t('chip_two'));
        if (/manual score/i.test(allFacts)) statement = this.t('air_statement');
      } else if (game.category === 'Bowling') {
        if (/spring[- ](?:powered|loaded)/i.test(allFacts)) chips.push(this.t('chip_bowling'));
        if (/resetting.*pins|pins.*resetting/i.test(allFacts)) chips.push(this.t('chip_reset'));
        if (/tabletop/i.test(product.title)) chips.push(this.t('chip_table'));
        if (chips.includes(this.t('chip_bowling')) && chips.includes(this.t('chip_reset'))) statement = this.t('bowl_statement');
      } else if (game.category === 'Foosball') {
        if (/smooth[- ]action rods/i.test(allFacts)) chips.push(this.t('chip_foos'));
        if (/manual score/i.test(allFacts)) chips.push(this.t('chip_score'));
        if (/players:\s*2\b/i.test(allFacts)) chips.push(this.t('chip_two'));
        if (/smooth[- ]action rods.*easy.grip/i.test(allFacts)) statement = this.t('foos_statement');
      } else if (game.category === 'Pool') {
        if (/felt/i.test(allFacts)) chips.push(this.t('chip_pool'));
        if (/no batteries required/i.test(allFacts)) chips.push(this.t('chip_battery_free'));
        if (/tabletop/i.test(product.title)) chips.push(this.t('chip_table'));
        if (/felt/i.test(allFacts) && /cue sticks/i.test(allFacts) && /balls/i.test(allFacts)) statement = this.t('pool_statement');
      }
      this.querySelector('[data-description]').textContent = statement;
      this.querySelector('[data-chips]').replaceChildren(...chips.slice(0, 3).map(chip => make('li', '', chip)));
      for (const [key, values] of Object.entries(facts)) {
        const target = this.querySelector(`[data-facts="${key}"]`);
        target.hidden = !values.length;
        const fallback = this.querySelector(`[data-facts-fallback="${key}"]`);
        fallback.hidden = !!values.length;
        fallback.textContent = this.t({ features:'details_unknown', includes:'includes_unknown', specs:'setup_unknown' }[key]);
        const list = make('ul'); list.append(...values.map(value => make('li', '', value)));
        target.replaceChildren(list);
      }
    }
    resized(src, width) {
      const url = new URL(src, location.origin);
      if (url.hostname.endsWith('shopify.com') || url.pathname.includes('/cdn/shop/')) url.searchParams.set('width', width);
      return url.href;
    }
    image(media, alt, width = 1200, eager = false, anonymous = false) {
      const image = make('img');
      if (anonymous) image.crossOrigin = 'anonymous';
      const source = media.src || media.preview_image?.src;
      image.src = this.resized(source, width);
      image.width = media.width || media.preview_image?.width || 1000;
      image.height = media.height || media.preview_image?.height || 1000;
      image.alt = alt;
      image.loading = eager ? 'eager' : 'lazy';
      if (width === 1200) {
        image.srcset = [400,600,800,1200].map(w => `${this.resized(source, w)} ${w}w`).join(',');
        image.sizes = '(max-width: 767px) 100vw, 600px';
      }
      if (eager) image.fetchPriority = 'high';
      return image;
    }
    rebuildMedia() {
      this.media.querySelectorAll('video').forEach(video => video.pause());
      const game = this.games.get(this.active.handle);
      const native = this.active.media || [];
      const ids = this.rcFamily?.galleryIds() || [game.heroId, ...game.galleryIds, game.actionId, ...native.filter(item => ['video', 'external_video'].includes(item.media_type)).map(item => String(item.id))].filter((id, i, ids) => id && ids.indexOf(id) === i);
      this.galleryItems = this.flyingSquad?.master ? this.flyingSquad.gallery() : ids.map(id => native.find(media => String(media.id) === id.trim())).filter(Boolean);
      const slides = document.createDocumentFragment(), thumbs = document.createDocumentFragment();
      this.galleryItems.forEach((item, index) => {
        const slide = make('div', 'arcade-gallery__slide ht-arcade__slide');
        if (item.media_type === 'image') {
          const photo = this.image(item, `${this.active.title} — ${index + 1}`, 1200, !index);
          slide.append(photo);
        }
        else if (item.media_type === 'video') {
          const video = make('video'); video.controls = true; video.playsInline = true; video.preload = 'none';
          if (item.preview_image?.src) video.poster = this.resized(item.preview_image.src, 800);
          item.sources?.forEach(source => { const node = make('source'); node.src = source.url; node.type = source.mime_type; video.append(node); }); slide.append(video);
        } else if (item.media_type === 'external_video') {
          if (item.preview_image?.src) slide.append(this.image({ ...item, src:item.preview_image.src }, this.active.title, 1200));
          const link = make('a', '', `▶ ${this.t('watch_video')}`); link.target = '_blank'; link.rel = 'noopener';
          link.href = item.host === 'youtube' ? `https://www.youtube.com/watch?v=${encodeURIComponent(item.external_id)}` : `https://vimeo.com/${encodeURIComponent(item.external_id)}`; slide.append(link);
        }
        const thumb = make('button'); thumb.type = 'button'; thumb.dataset.mediaIndex = index;
        thumb.setAttribute('aria-label', `${this.t('show_media')} ${index + 1}`); thumb.setAttribute('aria-pressed', String(!index));
        if (item.src || item.preview_image?.src) thumb.append(this.image({ ...item, src:item.preview_image?.src || item.src }, '', 120));
        if (item.media_type !== 'image') { const play = make('span', '', '▶'); play.setAttribute('aria-hidden', 'true'); thumb.append(play); }
        slides.append(slide); thumbs.append(thumb);
      });
      if (!this.galleryItems.length) slides.append(make('div', 'arcade-gallery__slide ht-arcade__slide', this.t('media_missing')));
      this.media.replaceChildren(slides); this.querySelector('[data-thumbs]').replaceChildren(thumbs);
      this.media.scrollLeft = 0; this.querySelector('[data-thumbs]').scrollLeft = 0; this.trackMedia();
    }
    mediaIndex() { return Math.round(this.media.scrollLeft / (this.media.clientWidth || 1)); }
    navigateMedia(index, behavior) {
      const count = this.galleryItems.length;
      if (!count) return;
      this.media.scrollTo({ left:((index + count) % count) * this.media.clientWidth, behavior:behavior || (matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth') });
    }
    trackMedia() {
      cancelAnimationFrame(this.mediaFrame);
      this.mediaFrame = requestAnimationFrame(() => {
        const active = Math.min(this.mediaIndex(), this.galleryItems.length - 1), rail = this.querySelector('[data-thumbs]');
        [...rail.children].forEach((thumb, index) => thumb.setAttribute('aria-pressed', String(index === active)));
        const thumb = rail.children[active];
        if (thumb) {
          const delta = thumb.getBoundingClientRect().left - rail.getBoundingClientRect().left;
          if (delta < 0 || delta + thumb.offsetWidth > rail.clientWidth) rail.scrollLeft += delta;
        }
        this.querySelector('[data-gallery-position]').textContent = this.galleryItems.length ? `${active + 1} / ${this.galleryItems.length}` : '';
        [...this.media.children].forEach((slide, index) => { if (index !== active) slide.querySelector('video')?.pause(); });
      });
    }
    finishPurchase(error) {
      if (!this.purchasing) return;
      if (this.added && this.checkout) { location.assign(`${this.base}checkout`); return; }
      this.purchasing = false;
      this.querySelector('[data-status]').textContent = error || (this.added ? this.t('added') : '');
      this.syncControls();
      if (this.routePending) { this.routePending = false; this.restoreRoute(); }
    }
  }
  if (!customElements.get('ht-arcade')) customElements.define('ht-arcade', Arcade);
  customElements.whenDefined('product-form').then(() => {
    if (customElements.get('arcade-purchase-form')) return;
    class ArcadePurchaseForm extends customElements.get('product-form') {
      connectedCallback() {
        this.finishedObserver = new MutationObserver(() => {
          if (!this.submitButton.classList.contains('loading')) this.closest('ht-arcade')?.finishPurchase(this.failure);
        });
        this.finishedObserver.observe(this.submitButton, { attributes:true, attributeFilter:['class'] });
      }
      disconnectedCallback() { this.finishedObserver?.disconnect(); }
      onSubmitHandler(event) {
        const arcade = this.closest('ht-arcade');
        if (arcade?.flyingSquad?.handles()) { event.preventDefault(); arcade.flyingSquad.submit(!!event.submitter?.hasAttribute('data-checkout')); return; }
        if (arcade?.isGuardedOffer()) {
          event.preventDefault();
          if(window.HobbytronCampaignBundles?.selection(arcade)&&!arcade.busy&&!arcade.purchasing&&!arcade.blocked){
            arcade.purchasing=true;arcade.syncControls();
            window.HobbytronCampaignBundles.purchase({root:arcade,checkout:!!event.submitter?.hasAttribute('data-checkout')}).then(result=>{arcade.querySelector('[data-status]').textContent=result.message;}).catch(error=>{arcade.querySelector('[data-status]').textContent=error.message;}).finally(()=>{arcade.purchasing=false;arcade.syncControls();if(arcade.routePending){arcade.routePending=false;arcade.restoreRoute();}});
            return;
          }
          arcade.querySelector('[data-status]').textContent = arcade.t('bundle_guard');
          return;
        }
        if (!arcade || arcade.busy || arcade.purchasing || arcade.blocked || !arcade.variant?.available || this.submitButton.getAttribute('aria-disabled') === 'true') { event.preventDefault(); return; }
        arcade.quantity(this.form.elements.quantity.value);
        arcade.checkout = !!event.submitter?.hasAttribute('data-checkout');
        arcade.added = false; arcade.purchasing = true; this.failure = '';
        arcade.querySelector('[data-status]').textContent = '';
        super.onSubmitHandler(event);
        arcade.syncControls();
      }
      dispatchCartErrorEvent(message, code) {
        this.failure = message; this.handleErrorMessage(message); super.dispatchCartErrorEvent(message, code);
      }
    }
    customElements.define('arcade-purchase-form', ArcadePurchaseForm);
  });
})();
